import React, { useState, useEffect, useRef } from 'react';
import { getDevices, getPositions, getSummaryReport, getEventsReport, getTripsReport, getPositionsByIds, knotsToMph, obdInfo, getGeofences, createGeofence, linkGeofenceToDevice, deleteGeofence, geocode, reverseGeocode, getGeofenceEvents, updateDevice, getSession, isLoggedIn, updateUser, logout, getStoredMe, getAllDevices, isAdmin, getAdminMe, deleteAccount } from '../traccar';
import AlertDetailModal from '../AlertDetailModal';
import { approvedForMember, shareIdentity } from '../memberLink';
import AddSharedVehicle from '../AddSharedVehicle';
import { askCar, parseAlertIntent, parseWatchIntent, parsePin, looksLikeAddress, getSuggested, detectLang, parseRentalLookup } from '../carQA';
import { addWatch } from '../carWatches';
import VehicleHistory from '../VehicleHistory';
import { getAlertPrefs, saveAlertPrefs, BUILTIN_ALERTS, CUSTOM_METRICS, HIGH_PRIORITY } from '../alertPrefs';
import { TagAlongWordmark } from '../TagAlongLogo';
import { playOpen } from '../clickSound';
import { getCustomers, updateCustomer } from '../customerStore';
import { useLang, translate, getLang } from '../i18n';
import PhotoPicker from '../PhotoPicker';
import { getVehiclePhotos } from '../photoStore';
import { getCurfew, saveCurfew } from '../curfewStore';
import AddressAutocomplete from '../AddressAutocomplete';

const M_TO_MI = 0.000621371;

// A sleeping tracker re-reports its last speed forever, so a parked car reads a
// "ghost" speed indefinitely. Show 0 unless the record is fresh AND the tracker
// says it's in motion — matches the map (getFleet), which already de-ghosts.
const deghostMph = (p) => {
  if (!p) return 0;
  const ls = Math.max(0, ...[p.serverTime, p.deviceTime, p.fixTime].map((t) => (t ? new Date(t).getTime() : 0)));
  const stale = Date.now() - ls > 7 * 60 * 1000;
  const raw = knotsToMph(p.speed);
  return (!stale && (p.attributes || {}).motion !== false && raw > 1) ? raw : 0;
};

// True inside the native iOS/Android app.
const IS_NATIVE = typeof window !== 'undefined' && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

// Local test hook: ?as=<account#> previews the app as that customer (no login,
// scoped to their cars) so you can test the customer experience on localhost.
const PREVIEW_AS = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('as')) || '';

// figure out which vehicle a question is about, from the device tag names
const NAME_STOP = new Set(['car', 'truck', 'van', 'suv', 'vehicle', 'ride', 'the', 'my', 'a', 'an', 'it']);
function resolveDevice(text, devices) {
  const t = ` ${text.toLowerCase()} `;
  let best = null, bestLen = 0;
  devices.forEach((d) => {
    const name = (((d.attributes || {}).displayName || d.name) || '').toLowerCase().trim();
    if (!name) return;
    if (t.includes(` ${name} `) && name.length > bestLen) { best = d; bestLen = name.length; }
    name.split(/[^a-z0-9]+/).forEach((tok) => {
      if (tok.length >= 3 && !NAME_STOP.has(tok) && new RegExp(`\\b${tok}\\b`).test(t) && tok.length > bestLen) { best = d; bestLen = tok.length; }
    });
  });
  return best;
}

// render plain text with clickable links; a bare Google-Maps URL shows as a pill
function linkify(text, onDark) {
  const parts = String(text || '').split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) => {
    if (!/^https?:\/\//.test(p)) return p;
    const isMap = /google\.com\/maps|maps\.google|maps\.app/.test(p);
    return (
      <a key={i} href={p} target="_blank" rel="noopener noreferrer"
        style={isMap
          ? { display: 'inline-flex', alignItems: 'center', gap: '4px', background: onDark ? 'rgba(255,255,255,0.2)' : '#0066ff', color: 'white', borderRadius: '999px', padding: '2px 10px', fontWeight: 700, fontSize: '12px', textDecoration: 'none', marginTop: '4px' }
          : { color: onDark ? '#dbeafe' : '#0066ff', textDecoration: 'underline', wordBreak: 'break-all' }}>
        {isMap ? '🗺️ Open in Google Maps' : p}
      </a>
    );
  });
}
// Native app uses a unified dark "liquid glass" skin; the web/admin view keeps
// the original light theme. TXT = default body text color for the active theme.
const TXT = IS_NATIVE ? '#e8ebf5' : '#0f172a';
const TXT_MUTED = IS_NATIVE ? '#9aa0b4' : '#64748b';
const lbl = { fontSize: '10px', fontWeight: 700, color: TXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.4px' };
const card = IS_NATIVE
  ? { background: 'rgba(255,255,255,0.055)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '20px', padding: '16px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07)', color: TXT }
  : { background: 'white', borderRadius: '14px', padding: '16px', boxShadow: '0 1px 3px rgba(15,23,42,0.08)' };
const inp = IS_NATIVE
  ? { padding: '11px 13px', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '12px', fontSize: '14px', width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', color: TXT }
  : { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', width: '100%', boxSizing: 'border-box', background: '#fff' };
// Inputs that sit on a WHITE surface (the My profile + delete-confirm modals).
// The shared `inp` uses the app's light-on-dark text colour, which renders
// nearly invisible on white — this forces readable dark text there.
const inpLight = { padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', width: '100%', boxSizing: 'border-box', background: '#fff', color: '#0f172a' };
const lblLight = { fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' };

export default function CarPage({ restrictIds = null } = {}) {
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [lang, setLang] = useLang(); // 'en' | 'es'
  const t = (k) => translate(lang, k);
  // keep the opening greeting in the chosen language until the user asks something
  useEffect(() => {
    setChat((c) => (c.length === 1 && c[0].greeting ? [{ from: 'car', text: translate(lang, 'greeting'), greeting: true }] : c));
  }, [lang]);
  const [avatarBig, setAvatarBig] = useState(false); // profile picture zoomed
  // membership plan comes from the logged-in customer's Traccar user attributes.
  // "talk" = two-way text/call with the car unlocked; otherwise tracking only.
  // (Admin/preview with no customer session keeps everything unlocked.)
  const [plan, setPlan] = useState(null);
  const [me, setMe] = useState(null); // logged-in customer's Traccar user (their profile)
  const [profileOpen, setProfileOpen] = useState(false);
  const [pForm, setPForm] = useState(null); // edit buffer for the profile modal
  const [delOpen, setDelOpen] = useState(false); // account-deletion confirm modal
  const [delText, setDelText] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const doDeleteAccount = async () => {
    setDelBusy(true);
    try {
      await deleteAccount();
      window.location.href = '/car?tagalong'; // signed out — back to login
    } catch (e) {
      setDelBusy(false);
      alert((lang === 'es' ? 'No se pudo eliminar la cuenta: ' : 'Could not delete account: ') + e.message);
    }
  };
  useEffect(() => {
    // preview: build a synthetic "logged-in customer" from the CRM record
    if (previewCust) {
      setMe({ id: 'preview', name: previewCust.name, email: previewCust.email, attributes: { account: previewCust.account, phone: previewCust.phone, photo: previewCust.photo, lang: previewCust.lang || 'en', cardLast4: previewCust.cardLast4, autopay: previewCust.autopay } });
      setPlan(previewCust.plan === 'talk' ? 'talk' : 'track');
      return;
    }
    if (!isLoggedIn()) { setPlan('admin'); return; }
    // prefer the identity cached at login (the session cookie is unreliable
    // cross-origin); fall back to a live session lookup if available.
    const stored = getStoredMe();
    if (stored) { setMe(stored); setPlan((stored.attributes && stored.attributes.plan) || 'track'); }
    else getSession().then((u) => { setMe(u); setPlan((u && u.attributes && u.attributes.plan) || 'track'); }).catch(() => setPlan('track'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Talking to the car is enabled for everyone for now — it runs entirely in-app
  // (no SMS/call/telephony cost), so it's on regardless of plan.
  const talkAllowed = true;
  // the customer's own profile (from their Traccar user) — used on their side
  const myAttrs = (me && me.attributes) || {};
  const openProfile = () => { setPForm({ name: me.name || '', phone: myAttrs.phone || '', email: me.email || '', photo: myAttrs.photo || '', lang: myAttrs.lang || lang, cardLast4: myAttrs.cardLast4 || '', autopay: !!myAttrs.autopay }); setProfileOpen(true); };
  const saveProfile = async () => {
    const updated = { ...me, name: pForm.name, email: pForm.email, attributes: { ...myAttrs, phone: pForm.phone, email: pForm.email, photo: pForm.photo, lang: pForm.lang, cardLast4: pForm.cardLast4, autopay: pForm.autopay } };
    // preview mode saves to the CRM record instead of the (absent) Traccar session
    if (me && me.id === 'preview' && previewCust) {
      updateCustomer(previewCust.id, { name: pForm.name, email: pForm.email, phone: pForm.phone, photo: pForm.photo, lang: pForm.lang, cardLast4: pForm.cardLast4, autopay: pForm.autopay });
      setMe(updated); if (pForm.lang && pForm.lang !== lang) setLang(pForm.lang); setProfileOpen(false); return;
    }
    try {
      const saved = await updateUser(updated);
      // keep the admin CRM record in sync with the customer's self-edit
      const crm = getCustomers().find((c) => c.account === myAttrs.account);
      if (crm) updateCustomer(crm.id, { name: pForm.name, email: pForm.email, phone: pForm.phone, photo: pForm.photo, lang: pForm.lang, cardLast4: pForm.cardLast4, autopay: pForm.autopay });
      setMe(saved || updated); if (pForm.lang && pForm.lang !== lang) setLang(pForm.lang); setProfileOpen(false);
    } catch (ex) { alert(ex.message); }
  };
  const [pickerOpen, setPickerOpen] = useState(false); // device picker popover
  const [pickerMode, setPickerMode] = useState('account'); // 'account' | 'all'
  const [expandedAcct, setExpandedAcct] = useState(null); // which account is expanded in the picker
  const [custPickSearch, setCustPickSearch] = useState(''); // filter the "by account" customer list
  const [stats, setStats] = useState(null);
  const [chat, setChat] = useState([{ from: 'car', text: translate(getLang(), 'greeting'), greeting: true }]);
  const [q, setQ] = useState('');
  const [thinking, setThinking] = useState(false);
  const [showSuggest, setShowSuggest] = useState(true); // example question chips (collapse after first send)
  const [geofences, setGeofences] = useState([]);
  const [events, setEvents] = useState([]);
  const [collapsedMap, setCollapsedMap] = useState({}); // per-vehicle collapse state
  const [placeName, setPlaceName] = useState('');
  const [placeAddr, setPlaceAddr] = useState('');
  const [placeAddrPt, setPlaceAddrPt] = useState(null); // coords picked from autocomplete
  const [devModal, setDevModal] = useState(null); // { mode:'add'|'remove', place, candidates:[{deviceId,name,record?}] }
  const [pickSel, setPickSel] = useState({}); // deviceId -> bool
  const [historyDevice, setHistoryDevice] = useState(null); // device whose trip map is open
  const [addr, setAddr] = useState(''); // reverse-geocoded current address for the selected car
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [carPhoto, setCarPhoto] = useState(null); // front/profile photo for the chat avatar
  const [alerts, setAlerts] = useState(null); // per-device alert prefs
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [openStat, setOpenStat] = useState(null);   // which driver-score card is expanded ('brakes'|'accel'|'speeding')
  const [statDetail, setStatDetail] = useState(null); // a single driving event whose detail is open
  const [curfew, setCurfew] = useState(null); // smart-curfew config
  const [curfewOpen, setCurfewOpen] = useState(false);
  const [newAlert, setNewAlert] = useState({ name: '', metric: 'speed', op: 'over', value: '', channel: 'app' });
  const [thresholds, setThresholds] = useState({}); // device-attribute threshold edits
  const [pending, setPending] = useState(null); // conversation waiting on a save/name answer
  const [places, setPlaces] = useState(() => { try { return JSON.parse(localStorage.getItem('dd-car-places')) || []; } catch { return []; } });
  const chatEnd = useRef(null);

  const savePlaces = (arr) => { setPlaces(arr); localStorage.setItem('dd-car-places', JSON.stringify(arr)); };
  const haversineMi = (x, y) => { const R = 3958.8, tr = (d) => d * Math.PI / 180; const dLat = tr(y.lat - x.lat), dLng = tr(y.lng - x.lng); const s = Math.sin(dLat / 2) ** 2 + Math.cos(tr(x.lat)) * Math.cos(tr(y.lat)) * Math.sin(dLng / 2) ** 2; return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)); };
  const findPlace = (lat, lng) => places.filter((p) => !p.deviceId || String(p.deviceId) === String(deviceId)).find((p) => haversineMi({ lat, lng }, { lat: p.lat, lng: p.lng }) < 0.1);
  // center of a Traccar geofence from its WKT area string
  const geoCenter = (area) => {
    if (!area) return null;
    const cm = String(area).match(/CIRCLE\s*\(\s*(-?[\d.]+)\s+(-?[\d.]+)\s*,/i);
    if (cm) return { lat: parseFloat(cm[1]), lng: parseFloat(cm[2]) };
    const pm = String(area).match(/POLYGON\s*\(\(\s*(-?[\d.]+)\s+(-?[\d.]+)/i);
    if (pm) return { lat: parseFloat(pm[1]), lng: parseFloat(pm[2]) };
    return null;
  };
  // Match a spoken place name to one the customer ALREADY saved — checks the local
  // list AND the server geofences, so it works on any device (phone included).
  const savedPlaceByName = (phrase) => {
    const ph = (phrase || '').toLowerCase().trim();
    if (!ph || ph.length < 2) return null;
    const strip = (n) => (n || '').replace(/^[⭐⏱]\s*/, '').trim();
    const near = (a, b) => a && b && (a === b || a.includes(b) || b.includes(a));
    const local = places.filter((p) => !p.deviceId || String(p.deviceId) === String(deviceId))
      .find((p) => near(strip(p.name).toLowerCase(), ph));
    if (local) return { name: strip(local.name), lat: local.lat, lng: local.lng };
    const g = geofences.find((gf) => near(strip(gf.name).toLowerCase(), ph));
    if (g) { const c = geoCenter(g.area); if (c) return { name: strip(g.name), lat: c.lat, lng: c.lng, geofenceId: g.id }; }
    return null;
  };

  // in preview mode, only show the previewed customer's cars (like a real login)
  const previewCust = PREVIEW_AS ? getCustomers().find((c) => c.account === PREVIEW_AS) : null;
  const [rawDevices, setRawDevices] = useState([]);
  const [sharedDevices, setSharedDevices] = useState([]); // family members' cars added by code
  const [sharedTick, setSharedTick] = useState(0);
  useEffect(() => {
    const loadDevices = () => getDevices().then(setRawDevices).catch(() => {});
    const load = () => getPositions().then(setPositions).catch(() => {});
    loadDevices();
    getGeofences().then(setGeofences).catch(() => {});
    load();
    // positions refresh often; the car LIST only needs an occasional refresh
    // (every 60s) plus whenever the tab returns to the foreground — this avoids
    // re-rendering the whole page every 10s (which bogged down large fleets).
    const posT = setInterval(load, 10000);
    const devT = setInterval(loadDevices, 60000);
    const onVis = () => { if (document.visibilityState === 'visible') loadDevices(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(posT); clearInterval(devT); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  // cars other owners shared with me (approved) — merged into my list
  useEffect(() => {
    const ident = shareIdentity(me);
    if (!ident || restrictIds) { setSharedDevices([]); return; }
    getAllDevices().then((all) => setSharedDevices(approvedForMember(all, ident.id))).catch(() => setSharedDevices([]));
  }, [me, restrictIds, sharedTick]);

  // Devices belong to a customer by attributes (customerId / account). Show the
  // right subset for who's viewing: a previewed customer, a real logged-in
  // customer (matched to their CRM record by traccar user id or email), or —
  // for admin — every device. Recomputes once we know who "me" is.
  useEffect(() => {
    // getDevices() already scopes a logged-in customer to their own cars, so
    // rawDevices is their set. Only preview needs an extra client-side filter.
    let d = previewCust ? rawDevices.filter((x) => (x.attributes || {}).customerId === previewCust.id) : rawDevices;
    // family/shared viewer: restrict to only the cars the owner approved for them
    if (restrictIds) { const ok = restrictIds.map(String); d = d.filter((x) => ok.includes(String(x.id))); }
    // merge in cars other owners shared with me (not in restricted/family view)
    else if (sharedDevices.length) {
      const have = new Set(d.map((x) => String(x.id)));
      d = [...d, ...sharedDevices.filter((x) => !have.has(String(x.id)))];
    }
    setDevices(d);
    // Which of these cars does the SIGNED-IN person actually own? An admin /
    // see-all login can see everyone's cars, and defaulting to the global first
    // one made it open a stranger's account (e.g. "Yeilen Flores"). So prefer a
    // car owned by "me" — matched by user id, member link, account, or customer
    // id — and only fall back to the first car if the viewer owns none.
    const mineFirst = (() => {
      if (previewCust) return d[0];
      if (!me) {
        // ADMIN with no customer identity: open on the ADMIN'S OWN car if they
        // have one, so signing in as nadim lands on nadim's account instead of
        // whichever customer happens to sort first.
        const adminMe = isAdmin() ? getAdminMe() : null;
        if (adminMe) {
          const auid = adminMe.id != null ? String(adminMe.id) : '';
          const aacct = adminMe.account ? String(adminMe.account) : '';
          const aemail = adminMe.email ? adminMe.email.toLowerCase() : '';
          const crm = getCustomers().find((c) => (auid && String(c.traccarUserId) === auid) || (aemail && c.email && c.email.toLowerCase() === aemail));
          const acid = crm ? String(crm.id) : '';
          const ownedByAdmin = (dev) => {
            const x = dev.attributes || {};
            const ow = Array.isArray(x.owners) ? x.owners : [];
            if (auid && (String(x.ownerUserId || '') === auid || ow.some((o) => o && String(o.userId) === auid))) return true;
            if (acid && (String(x.customerId || '') === acid || ow.some((o) => o && String(o.customerId) === acid))) return true;
            if (aacct && aacct.toUpperCase() !== 'TA' && (String(x.account || '') === aacct || ow.some((o) => o && String(o.account) === aacct))) return true;
            return false;
          };
          const mine = d.find(ownedByAdmin);
          if (mine) return mine;
        }
        return d[0];
      }
      const uid = me.id != null ? String(me.id) : '';
      const memberId = uid ? `u${uid}` : '';
      const crm = getCustomers().find((c) => (uid && String(c.traccarUserId) === uid) || (me.email && c.email && c.email.toLowerCase() === me.email.toLowerCase()));
      const cid = (crm && crm.id) || (myAttrs.customerId || '');
      const acct = myAttrs.account || (crm && crm.account) || '';
      const ownedByMe = (dev) => {
        const x = dev.attributes || {};
        if (uid && x.ownerUserId != null && String(x.ownerUserId) === uid) return true;
        if (uid && Array.isArray(x.owners) && x.owners.some((o) => o && String(o.userId) === uid)) return true;
        if (memberId && Array.isArray(x.memberLinks) && x.memberLinks.some((m) => m && m.memberId === memberId && m.status === 'approved')) return true;
        if (acct && String(x.account) === String(acct)) return true;
        if (cid && String(x.customerId) === String(cid)) return true;
        return false;
      };
      return d.find(ownedByMe) || d[0];
    })();
    setDeviceId((prev) => (prev && d.some((x) => String(x.id) === String(prev)) ? prev : (mineFirst ? String(mineFirst.id) : '')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDevices, me, restrictIds, sharedDevices]);

  const position = positions.find((p) => String(p.deviceId) === String(deviceId));
  const device = devices.find((d) => String(d.id) === String(deviceId));

  // group devices by the customer account they belong to (for the picker)
  const accountGroups = (() => {
    const custs = getCustomers();
    const byId = {}; custs.forEach((c) => { byId[c.id] = c; });
    const groups = [];
    const idx = {};
    devices.forEach((d) => {
      const a = d.attributes || {};
      let cid = a.customerId || '';
      // A car assigned by USER ID (e.g. via the Devices tab) can have no
      // customerId — resolve its owner from ownerUserId so it still groups with
      // that account instead of falling into "unassigned".
      if ((!cid || !byId[cid]) && a.ownerUserId != null) {
        const c = custs.find((x) => String(x.traccarUserId) === String(a.ownerUserId));
        if (c) cid = c.id;
      }
      const key = cid && byId[cid] ? cid : '_none';
      if (idx[key] == null) { idx[key] = groups.length; groups.push({ key, customer: byId[cid] || null, devices: [] }); }
      groups[idx[key]].devices.push(d);
    });
    // customers first (by account #), unassigned last
    return groups.sort((a, b) => {
      if (a.key === '_none') return 1; if (b.key === '_none') return -1;
      return (a.customer.account || '').localeCompare(b.customer.account || '');
    });
  })();
  const currentCust = (() => { const cid = (device && (device.attributes || {}).customerId) || ''; return getCustomers().find((c) => c.id === cid) || null; })();
  // The cars to list = every car owned by ONE anchor account. The anchor is a
  // STABLE identity (not the selected car), so switching cars within an account
  // never changes the list, and a car CO-OWNED by two accounts (e.g. a customer
  // + admin) only lists under the account being viewed — it does not drag the
  // other owner's cars in.
  //   • customer view       → the signed-in customer (me)
  //   • admin "view as"      → the previewed customer
  //   • admin see-all        → the customer of the open car (currentCust)
  //   • last resort          → the open car's own primary owner
  const accountDevices = (() => {
    if (!device) return [];
    let uid = '', cid = '', acct = '';
    if (previewCust) {
      uid = String(previewCust.traccarUserId || ''); cid = String(previewCust.id || ''); acct = String(previewCust.account || '');
    } else if (me && me.id !== 'preview') {
      uid = String(me.id || '');
      const c = getCustomers().find((x) => String(x.traccarUserId) === uid || (me.email && x.email && x.email.toLowerCase() === me.email.toLowerCase()));
      cid = String((c && c.id) || (myAttrs.customerId) || '');
      acct = String((c && c.account) || (myAttrs.account) || '');
    } else if (currentCust) {
      uid = String(currentCust.traccarUserId || ''); cid = String(currentCust.id || ''); acct = String(currentCust.account || '');
    }
    // fallback: no resolved account → use the open car's own primary owner
    if (!uid && !cid && !acct) {
      const x = device.attributes || {};
      uid = String(x.ownerUserId || ''); cid = String(x.customerId || ''); acct = String(x.account || '');
    }
    const memberId = uid ? `u${uid}` : '';
    const ownedByAnchor = (d) => {
      const x = d.attributes || {};
      const owners = Array.isArray(x.owners) ? x.owners : [];
      if (uid) {
        if (String(x.ownerUserId || '') === uid) return true;
        if (owners.some((o) => o && String(o.userId) === uid)) return true;
        if (Array.isArray(x.memberLinks) && x.memberLinks.some((m) => m && m.memberId === memberId && m.status === 'approved')) return true;
      }
      if (cid) {
        if (String(x.customerId || '') === cid) return true;
        if (owners.some((o) => o && String(o.customerId) === cid)) return true;
      }
      // account match, but never the house "TA" pool (that's not a real account)
      if (acct && acct.toUpperCase() !== 'TA') {
        if (String(x.account || '') === acct) return true;
        if (owners.some((o) => o && String(o.account) === acct)) return true;
      }
      return false;
    };
    const list = devices.filter(ownedByAnchor);
    return list.length ? list : [device];
  })();
  const pickDevice = (id) => { setDeviceId(String(id)); setPickerOpen(false); };
  // pick a whole account: load its first car (the page then lists all its cars)
  const pickAccount = (g) => { if (g.devices[0]) { setDeviceId(String(g.devices[0].id)); } setPickerOpen(false); };
  // Cars belonging to a customer, matched by ALL owner signals (user id,
  // customer id, account, owners[], approved family link) — so a car links to
  // its customer even if its primary attribute is off. Used by the customer
  // switcher so every customer shows their real car count.
  const custDeviceList = (cust) => {
    if (!cust) return [];
    const uid = cust.traccarUserId != null ? String(cust.traccarUserId) : '';
    const cid = String(cust.id || '');
    const acct = String(cust.account || '');
    const memberId = uid ? `u${uid}` : '';
    return devices.filter((d) => {
      const x = d.attributes || {};
      const ow = Array.isArray(x.owners) ? x.owners : [];
      if (uid && (String(x.ownerUserId || '') === uid || ow.some((o) => o && String(o.userId) === uid))) return true;
      if (uid && Array.isArray(x.memberLinks) && x.memberLinks.some((m) => m && m.memberId === memberId && m.status === 'approved')) return true;
      if (cid && (String(x.customerId || '') === cid || ow.some((o) => o && String(o.customerId) === cid))) return true;
      if (acct && acct.toUpperCase() !== 'TA' && (String(x.account || '') === acct || ow.some((o) => o && String(o.account) === acct))) return true;
      return false;
    });
  };

  // A position can exist (OBD data: voltage, speed) with NO GPS fix — the C300
  // reported 0,0/blank coords, which left the address stuck on "Locating…" and
  // the car off the map. Treat only a finite, non-zero lat/lng as a real fix.
  const hasFix = !!(position
    && Number.isFinite(Number(position.latitude)) && Number.isFinite(Number(position.longitude))
    && !(Number(position.latitude) === 0 && Number(position.longitude) === 0));
  // show a readable address under the car (reverse-geocode when it moves)
  const latKey = hasFix ? Math.round(position.latitude * 3000) : null;
  const lngKey = hasFix ? Math.round(position.longitude * 3000) : null;
  useEffect(() => {
    setAddr('');
    if (!hasFix) return;
    if (position.address) { setAddr(position.address); return; }
    let cancelled = false;
    reverseGeocode(position.latitude, position.longitude).then((r) => { if (!cancelled && r) setAddr(r.display || ''); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, latKey, lngKey]);

  useEffect(() => { if (deviceId) { setAlerts(getAlertPrefs(deviceId)); setCurfew(getCurfew(deviceId)); } }, [deviceId]);
  const setCf = (patch) => setCurfew((c) => ({ ...c, ...patch }));
  const saveCurfewCfg = () => { saveCurfew(deviceId, curfew); setCurfewOpen(false); };
  // Per-car "alert against the posted speed limit" toggle. Stored as a Traccar
  // device attribute so the backend poller can read it and check speed-vs-limit
  // even when the phone is locked.
  const speedLimitOn = !!((device && device.attributes) || {}).taSpeedLimitAlert;
  const [savingSpd, setSavingSpd] = useState(false);
  const toggleSpeedLimit = async (on) => {
    if (!device) return;
    setSavingSpd(true);
    const updated = { ...device, attributes: { ...(device.attributes || {}), taSpeedLimitAlert: on } };
    try { await updateDevice(updated); setDevices((ds) => ds.map((d) => (d.id === device.id ? updated : d))); } catch { /* ignore */ }
    setSavingSpd(false);
  };

  // profile photo for the chat avatar — the front photo they uploaded on the vehicle profile
  useEffect(() => {
    let live = true;
    if (!deviceId) { setCarPhoto(null); return undefined; }
    getVehiclePhotos(deviceId).then((p) => { if (live) setCarPhoto((p && (p.front || p.left || p.right || p.back)) || null); }).catch(() => { if (live) setCarPhoto(null); });
    return () => { live = false; };
  }, [deviceId]);

  const setBuiltin = (key, patch) => setAlerts((a) => ({ ...a, builtins: { ...a.builtins, [key]: { ...a.builtins[key], ...patch } } }));
  const setCustom = (id, patch) => setAlerts((a) => ({ ...a, custom: a.custom.map((c) => (c.id === id ? { ...c, ...patch } : c)) }));
  const removeCustom = (id) => setAlerts((a) => ({ ...a, custom: a.custom.filter((c) => c.id !== id) }));
  const addCustomAlert = () => {
    if (!newAlert.name.trim() || newAlert.value === '') { alert('Give the alert a name and a value.'); return; }
    setAlerts((a) => ({ ...a, custom: [...a.custom, { id: `A${Date.now()}`, on: true, ...newAlert, value: Number(newAlert.value) }] }));
    setNewAlert({ name: '', metric: 'speed', op: 'over', value: '', channel: 'app' });
  };
  const thVal = (attr) => (thresholds[attr] !== undefined ? thresholds[attr] : ((device && device.attributes && device.attributes[attr]) != null ? device.attributes[attr] : ''));
  const saveAlerts = async () => {
    saveAlertPrefs(deviceId, alerts);
    if (device && Object.keys(thresholds).length) {
      const attrs = { ...(device.attributes || {}) };
      Object.entries(thresholds).forEach(([k, v]) => { if (v === '' || v == null) delete attrs[k]; else attrs[k] = Number(v); });
      const updated = { ...device, attributes: attrs };
      try { await updateDevice(updated); setDevices((ds) => ds.map((d) => (d.id === device.id ? updated : d))); } catch { /* ignore */ }
    }
    setThresholds({});
    setAlertsOpen(false);
  };
  const metricOf = (k) => CUSTOM_METRICS.find((m) => m.key === k) || {};
  // Delivery is in-app only for now (text/call come back when telephony is wired),
  // so this is a simple static badge rather than a three-way switch.
  const chanToggle = () => (
    <span style={{
      flexShrink: 0, fontSize: '10.5px', fontWeight: 700, padding: '5px 11px', borderRadius: '999px',
      background: IS_NATIVE ? 'rgba(59,130,246,0.18)' : '#eff6ff',
      border: `1px solid ${IS_NATIVE ? 'rgba(59,130,246,0.35)' : '#dbeafe'}`,
      color: IS_NATIVE ? '#93c5fd' : '#1d4ed8', whiteSpace: 'nowrap',
    }}>🔔 In-app</span>
  );

  // today's stats + recent geofence events for the selected car
  useEffect(() => {
    if (!deviceId) return;
    const now = new Date(); const start = new Date(now); start.setHours(0, 0, 0, 0);
    const from = start.toISOString(), to = now.toISOString();
    Promise.all([
      getSummaryReport(deviceId, from, to).catch(() => null),
      getEventsReport(deviceId, from, to).catch(() => []),
      getTripsReport(deviceId, from, to).catch(() => []),
    ]).then(async ([s, ev, tr]) => {
      // Keep the actual events (not just counts) so each driver-score card can
      // drop down its list, and tapping one opens its full location detail.
      const pick = (re) => ev.filter((e) => e.attributes && re.test(e.attributes.alarm || ''));
      const brakeEv = pick(/brak/i), accelEv = pick(/accel/i), speedEv = pick(/overspeed/i);
      const all = [...brakeEv, ...accelEv, ...speedEv];
      const posIds = [...new Set(all.map((e) => e.positionId).filter(Boolean))];
      let posById = {};
      try { const pl = await getPositionsByIds(posIds); posById = Object.fromEntries((pl || []).map((p) => [p.id, p])); } catch { /* ignore */ }
      const nm = device ? ((device.attributes || {}).displayName || device.name) : '';
      const toItem = (e, label) => {
        const p = posById[e.positionId];
        const when = e.eventTime || e.serverTime || e.fixTime;
        return {
          t: when, label,
          place: p && p.latitude != null ? {
            lat: p.latitude, lng: p.longitude,
            speed: Math.round((p.speed || 0) * 1.15078),
            at: when ? new Date(when).getTime() : Date.now(),
            imei: device ? device.uniqueId : '', device: nm,
          } : null,
        };
      };
      const newest = (a, b) => new Date(b.t) - new Date(a.t);
      setStats({
        miles: s ? (s.distance * M_TO_MI) : 0,
        topSpeed: s ? knotsToMph(s.maxSpeed) : 0,
        trips: tr.length,
        brakes: brakeEv.length, accel: accelEv.length, speeding: speedEv.length,
        brakeEvents: brakeEv.map((e) => toItem(e, 'Hard brake')).sort(newest),
        accelEvents: accelEv.map((e) => toItem(e, 'Hard acceleration')).sort(newest),
        speedEvents: speedEv.map((e) => toItem(e, 'Speeding')).sort(newest),
      });
    });
    setOpenStat(null);
    getGeofenceEvents(deviceId, 24 * 60).then((e) => setEvents(e.slice(-6).reverse())).catch(() => setEvents([]));
  }, [deviceId]);

  useEffect(() => { chatEnd.current && chatEnd.current.scrollIntoView({ behavior: 'smooth' }); }, [chat, thinking]);

  const say = (text) => setChat((c) => [...c, { from: 'car', text }]);
  const dirWord = (d) => (d === 'leave' ? 'leaves' : 'arrives at');

  // create the geofence watch; save it as a named place only if `saved`
  // create a geofence for one device and return the local place record
  const createPlaceOnDevice = async (devId, name, lat, lng, address) => {
    const g = await createGeofence(`⭐ ${name}`, lat, lng, 200).catch(() => null);
    if (g && g.id) await linkGeofenceToDevice(devId, g.id).catch(() => {});
    return { name, lat, lng, address: address || '', deviceId: devId, geofenceId: g && g.id };
  };

  const createWatch = async (label, lat, lng, saved, address) => {
    const g = await createGeofence(`${saved ? '⭐' : '⏱'} ${label}`, lat, lng, 200).catch(() => null);
    if (g && g.id) {
      // Saved places are account-wide: link the geofence to EVERY car so an
      // arrival alert fires no matter which car reaches it. A one-time watch
      // (saved === false) stays on just the current car.
      const targets = (saved && devices.length) ? devices.map((d) => d.id) : [deviceId];
      for (const id of targets) { await linkGeofenceToDevice(id, g.id).catch(() => {}); } // eslint-disable-line no-await-in-loop
    }
    getGeofences().then(setGeofences).catch(() => {});
    if (saved) savePlaces([...places, { name: label, lat, lng, address: address || '', deviceId, geofenceId: g && g.id }]);
  };

  const openAddToOthers = (place) => {
    const others = devices.filter((d) => String(d.id) !== String(deviceId));
    if (others.length === 0) return;
    setPickSel({});
    setDevModal({ mode: 'add', place, candidates: others.map((d) => ({ deviceId: d.id, name: dName(d) || d.name })) });
  };

  // remove a saved place from the current car; then offer to remove matching ones elsewhere
  const removePlace = async (rec) => {
    if (rec.geofenceId) await deleteGeofence(rec.geofenceId).catch(() => {});
    const rest = places.filter((p) => p !== rec && !(p.geofenceId && p.geofenceId === rec.geofenceId));
    savePlaces(rest);
    getGeofences().then(setGeofences).catch(() => {});
    const same = (p) => p.name && rec.name && p.name.toLowerCase() === rec.name.toLowerCase();
    const others = rest.filter((p) => String(p.deviceId) !== String(deviceId) && same(p));
    if (others.length) {
      setPickSel({});
      setDevModal({ mode: 'remove', place: rec, candidates: others.map((p) => ({ deviceId: p.deviceId, name: dName(devices.find((d) => String(d.id) === String(p.deviceId))) || `Unit ${p.deviceId}`, record: p })) });
    }
  };

  const confirmDevModal = async () => {
    const m = devModal;
    if (m.mode === 'add') {
      const recs = [];
      for (const c of m.candidates) { if (pickSel[c.deviceId]) recs.push(await createPlaceOnDevice(c.deviceId, m.place.name, m.place.lat, m.place.lng, m.place.address)); }
      if (recs.length) savePlaces([...places, ...recs]);
    } else {
      const toRemove = m.candidates.filter((c) => pickSel[c.deviceId]);
      for (const c of toRemove) { if (c.record.geofenceId) await deleteGeofence(c.record.geofenceId).catch(() => {}); }
      const gids = new Set(toRemove.map((c) => c.record.geofenceId));
      savePlaces(places.filter((p) => !gids.has(p.geofenceId)));
    }
    getGeofences().then(setGeofences).catch(() => {});
    setDevModal(null);
  };
  const allPicked = devModal && devModal.candidates.every((c) => pickSel[c.deviceId]);
  const toggleAll = () => { const v = !allPicked; const next = {}; devModal.candidates.forEach((c) => { next[c.deviceId] = v; }); setPickSel(next); };

  // handle the user's reply to a save/name question
  const handlePending = async (t) => {
    const p = pending; const low = t.toLowerCase();
    const affirm = /^(yes|yeah|yep|sure|ok|okay|save|keep|please|correct|that'?s? right|do it)\b/i.test(low);
    // "just this once" / decline saving
    if (/just once|one time|onetime|only once|don'?t save|do not save|no save|^no\b|temporar|skip/i.test(low)) {
      await createWatch(p.address, p.lat, p.lng, false); setPending(null);
      say(`Okay — I'll watch ${p.address} this one time and let you know when the car ${dirWord(p.direction)} there. It won't be saved. Give it a name anytime to keep it.`);
      return;
    }
    // "yes" and we already proposed a name → save it under that name
    if (affirm && p.guess) {
      await createWatch(p.guess, p.lat, p.lng, true, p.address); setPending(null);
      say(`Saved as "${p.guess}". From now on I'll alert you whenever the car ${dirWord(p.direction)} ${p.guess}.`);
      return;
    }
    // bare "yes" with no proposed name → ask for one
    if (affirm && !p.guess && low.replace(/^(yes|yeah|yep|sure|ok|okay|save|please|do it)\b[,\s]*/i, '').trim().length < 2) {
      setPending({ ...p, guess: '' }); say('Great — what should I call it?'); return;
    }
    // otherwise treat the message as the name
    const name = t.replace(/^(yes,?\s*|save (it )?as\s*|call it\s*|name it\s*|it'?s\s*)/i, '').replace(/[.?!]+$/, '').trim();
    if (name.length >= 2) {
      await createWatch(name, p.lat, p.lng, true, p.address); setPending(null);
      say(`Saved as "${name}". From now on I'll alert you whenever the car ${dirWord(p.direction)} ${name}.`);
      return;
    }
    say('Reply with a name to save it (like Home or School), or say "just once".');
  };

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setChat((c) => [...c, { from: 'me', text }]);
    setShowSuggest(false); // tuck the example questions away once the conversation starts
    setQ('');
    // reply in the language of the question (fall back to the app language)
    const askLang = detectLang(text) || lang;
    const LL = (en, es) => (askLang === 'es' ? es : en);

    if (pending) { await handlePending(text); return; }

    setThinking(true);

    // which car is this about? (by tag name) — else the selected one
    const target = resolveDevice(text, devices);
    const useId = target ? String(target.id) : deviceId;
    const usePos = positions.find((p) => String(p.deviceId) === String(useId)) || position;
    const useDev = target || device;
    const carName = target ? dName(target) : (device ? dName(device) : 'the car');
    const prefix = target && String(target.id) !== String(deviceId) ? `${dName(target)}: ` : '';
    if (target && String(target.id) !== String(deviceId)) setDeviceId(useId); // follow the car they asked about

    // time-boxed watch ("for the next 30 min, count how many times it turns on")
    const watch = parseWatchIntent(text);
    if (watch) {
      const minutes = watch.minutes || (watch.mode === 'count' ? 60 : 240);
      addWatch({ deviceId: useId, event: watch.event, mode: watch.mode, minutes, until: new Date(Date.now() + minutes * 60000).toISOString() });
      setThinking(false);
      const ev = LL(
        watch.event === 'ignitionOn' ? 'turns on' : watch.event === 'ignitionOff' ? 'turns off' : 'starts moving',
        watch.event === 'ignitionOn' ? 'se prenda' : watch.event === 'ignitionOff' ? 'se apague' : 'empiece a moverse');
      say(watch.mode === 'count'
        ? LL(`Okay — for the next ${minutes} minutes I'll count every time ${carName} ${ev} and give you the total when the time's up. Each one also pops an alert.`,
            `Listo — durante los próximos ${minutes} minutos contaré cada vez que ${carName} ${ev} y te daré el total cuando termine el tiempo. Cada una también genera una alerta.`)
        : LL(`Okay — I'll alert you every time ${carName} ${ev}, for the next ${minutes} minutes.`,
            `Listo — te avisaré cada vez que ${carName} ${ev}, durante los próximos ${minutes} minutos.`));
      return;
    }

    const pin = parsePin(text);
    const intent = parseAlertIntent(text);
    const bareAddr = !pin && !intent && looksLikeAddress(text) ? text : null;

    if (pin || intent || bareAddr) {
      const direction = intent ? intent.direction : (/(leave|left|depart|exit)/i.test(text) ? 'leave' : 'arrive');
      // FIRST: is the spoken place one they've already saved? (match by name, not geocode)
      const spoken = (intent && (intent.name || intent.address)) || bareAddr || '';
      const savedHit = !pin && savedPlaceByName(spoken);
      if (savedHit) {
        setThinking(false);
        await createWatch(savedHit.name, savedHit.lat, savedHit.lng, false);
        say(LL(`Done — I'll alert you when the car ${dirWord(direction)} ${savedHit.name} (saved place).`, `Listo — te avisaré cuando el auto ${direction === 'leave' ? 'salga de' : 'llegue a'} ${savedHit.name} (lugar guardado).`));
        return;
      }
      let lat, lng, address;
      if (pin) { lat = pin.lat; lng = pin.lng; address = `dropped pin (${lat.toFixed(5)}, ${lng.toFixed(5)})`; }
      else {
        const addr = intent ? intent.address : bareAddr;
        const pt = await geocode({ address: addr });
        if (!pt) { setThinking(false); say(LL(`I couldn't find "${addr}". Try a full street address, or drop a pin (paste coordinates or a Google Maps link).`, `No pude encontrar "${addr}". Prueba con una dirección completa, o comparte un pin (pega coordenadas o un enlace de Google Maps).`)); return; }
        lat = pt.lat; lng = pt.lng; address = pt.formatted || addr;
      }
      setThinking(false);

      const existing = findPlace(lat, lng);
      if (existing) { await createWatch(existing.name, lat, lng, false); say(LL(`Done — I'll alert you when the car ${dirWord(direction)} ${existing.name} (saved place).`, `Listo — te avisaré cuando el auto ${direction === 'leave' ? 'salga de' : 'llegue a'} ${existing.name} (lugar guardado).`)); return; }

      const guess = intent && intent.name ? intent.name.charAt(0).toUpperCase() + intent.name.slice(1) : '';
      setPending({ stage: guess ? 'name?' : 'save?', direction, address, lat, lng });
      if (guess) say(LL(`That's a new spot: ${address}. Save it as "${guess}"? Reply "yes", give a different name, or say "just once" to watch it one time only.`, `Es un lugar nuevo: ${address}. ¿Lo guardo como "${guess}"? Responde "sí", da otro nombre, o di "solo una vez" para vigilarlo una sola vez.`));
      else say(LL(`That's a new spot: ${address}. Want me to save it? Reply with a name (like Home or School) to keep it, or say "just once" to watch it this one time only.`, `Es un lugar nuevo: ${address}. ¿Quieres que lo guarde? Responde con un nombre (como Casa o Escuela) para conservarlo, o di "solo una vez" para vigilarlo una sola vez.`));
      return;
    }

    const answer = await askCar(text, { deviceId: useId, position: usePos, device: useDev, lang, places, devices });
    setThinking(false);
    const wantsMap = /where|location|went|been|trip|weekend|yesterday|\bhome\b|school|work|stop|parked|drove|route|history|alert|brak|speed|idl|aggress|d[óo]nde|ubicaci[óo]n|fue|estuvo|viaje|fin de semana|ayer|casa|escuela|trabajo|par[óo]|estacionad|manej[óo]|ruta|historial|fren|velocidad/i.test(text);
    setChat((c) => [...c, { from: 'car', text: prefix + answer, mapDevice: wantsMap ? useDev : null, openRentals: !!parseRentalLookup(text) }]);
  };

  const addPlace = async () => {
    if (!placeName.trim() || !placeAddr.trim()) { alert('Enter a place name and address.'); return; }
    const pt = (placeAddrPt && placeAddrPt.lat != null) ? placeAddrPt : await geocode({ address: placeAddr });
    if (!pt) { alert("Couldn't find that address. Pick one from the suggestions, or add a city, state, or ZIP."); return; }
    const name = placeName.trim(); const addr = placeAddr.trim();
    const rec = await createPlaceOnDevice(deviceId, name, pt.lat, pt.lng, addr);
    savePlaces([...places, rec]);
    setPlaceName(''); setPlaceAddr(''); setPlaceAddrPt(null); getGeofences().then(setGeofences);
    // if the customer has other cars, offer to add the same place to them
    openAddToOthers({ name, lat: pt.lat, lng: pt.lng, address: addr });
  };

  const a = (position && position.attributes) || {};
  const obd = obdInfo(a);
  // Freshness first (last check-in, not the GPS fix time — the fix can lag minutes
  // in dense areas while live OBD keeps coming), so we can de-ghost the speed.
  const lastSeen = position ? Math.max(0, ...[position.serverTime, position.deviceTime, position.fixTime].map((t) => (t ? new Date(t).getTime() : 0))) : 0;
  const stalePos = !position || (Date.now() - lastSeen > 7 * 60 * 1000);
  const rawMph = position ? knotsToMph(position.speed) : 0;
  // A sleeping tracker keeps re-reporting its LAST speed forever, so a parked car
  // reads "25 mph" indefinitely. Only trust the speed when the record is fresh AND
  // the tracker says it's moving — otherwise the car is parked → 0. This matches
  // the map (getFleet), which already de-ghosted; the car page wasn't.
  const moving = !stalePos && a.motion !== false && rawMph > 1;
  const mph = moving ? rawMph : 0;
  // robust "engine on" — decided by RPM, NOT the ignition bit. On these OBD cars
  // the ignition flag reads TRUE at rest voltage (~12.8V) while the engine is off,
  // which showed a parked car as "ON". RPM can't lie (an OBD tracker only sees it
  // when the engine turns); charging voltage (>13.2V = alternator) and movement
  // corroborate. The flaky ignition flag is deliberately left out.
  const on = !stalePos && (moving || (Number(a.io36) || 0) > 200 || (Number(a.power) || 0) >= 13.2);
  const st = (label, val, color) => {
    let c = color || TXT;
    if (IS_NATIVE) { if (c === '#0f172a') c = TXT; else if (c === '#b45309') c = '#fbbf24'; else if (c === '#dc2626') c = '#f87171'; }
    const glass = IS_NATIVE ? { background: 'linear-gradient(160deg, rgba(255,255,255,0.09), rgba(255,255,255,0.03))' } : {};
    return <div style={{ ...card, ...glass, textAlign: 'center', padding: '16px 10px', borderRadius: '18px' }}><div style={lbl}>{label}</div><div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '24px', color: c, marginTop: '5px' }}>{val}</div></div>;
  };
  // like st(), but tappable: shows the count and, when there are events, drops
  // down the list of them below the grid (openStat), each opening its detail.
  const stTap = (key, label, events, color) => {
    const count = events ? events.length : null;
    const active = openStat === key;
    const clickable = count > 0;
    let c = color || TXT;
    if (IS_NATIVE) { if (c === '#0f172a') c = TXT; else if (c === '#b45309') c = '#fbbf24'; else if (c === '#dc2626') c = '#f87171'; }
    const glass = IS_NATIVE ? { background: 'linear-gradient(160deg, rgba(255,255,255,0.09), rgba(255,255,255,0.03))' } : {};
    return (
      <div onClick={clickable ? () => { playOpen(); setOpenStat(active ? null : key); } : undefined}
        style={{ ...card, ...glass, textAlign: 'center', padding: '16px 10px', borderRadius: '18px', cursor: clickable ? 'pointer' : 'default', outline: active ? '2px solid rgba(96,165,250,0.55)' : 'none' }}>
        <div style={lbl}>{label}</div>
        <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '24px', color: c, marginTop: '5px' }}>{count == null ? '—' : count}</div>
        {clickable && <div style={{ fontSize: '10px', fontWeight: 700, color: IS_NATIVE ? '#93c5fd' : '#2563eb', marginTop: '3px' }}>{active ? 'hide ▲' : 'tap ▾'}</div>}
      </div>
    );
  };
  // light card tints only apply on the web theme; native uses the dark glass base
  const tint = (bg, border) => (IS_NATIVE ? {} : { background: bg, border });
  const collapsed = !!collapsedMap[deviceId];
  const toggleCollapse = () => setCollapsedMap((m) => ({ ...m, [deviceId]: !m[deviceId] }));
  const myPlaces = places.filter((p) => !p.deviceId || String(p.deviceId) === String(deviceId));

  // the customer's own name for a car (kept in an attribute so the backend
  // keeps the account-number name, e.g. "7051-6474", untouched)
  const dName = (d) => (d ? ((d.attributes || {}).displayName || d.name) : '');
  const saveName = async () => {
    const name = nameDraft.trim();
    setRenaming(false);
    if (!device || name === dName(device)) return;
    // store the friendly name in attributes.displayName; leave device.name (account #) as-is
    const updated = { ...device, attributes: { ...(device.attributes || {}), displayName: name } };
    try { await updateDevice(updated); setDevices((ds) => ds.map((d) => (d.id === device.id ? updated : d))); }
    catch { alert('Could not rename the device.'); }
  };

  return (
    <div className="page-container">
      {/* whose profile you're viewing */}
      {me && !IS_NATIVE && (
        <div style={{ background: 'linear-gradient(135deg,#fff3ec,#ffe7d8)', border: '1px solid #ffd9c2', borderRadius: '12px', padding: '10px 16px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          {(myAttrs.photo) ? (
            <img src={myAttrs.photo} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #f8703c' }} />
          ) : (
            <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg,#f8703c,#f4511e)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '13px' }}>{((me.name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('') || '?').toUpperCase()}</div>
          )}
          <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '16px', color: '#c2410c' }}>
            {lang === 'es' ? '¡Hola' : 'Hi'}, {(me.name || '').split(/\s+/)[0] || (lang === 'es' ? 'cliente' : 'there')}!
          </span>
          {myAttrs.account && <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8' }}>#{myAttrs.account}</span>}
        </div>
      )}
      {/* Admin (not a real customer): make it explicit you're viewing a customer's
          app, not that you ARE that customer. The name below is whose cars you're
          browsing; tap another car to switch accounts. */}
      {isAdmin() && !PREVIEW_AS && !me && !IS_NATIVE && (
        <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '10px 16px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800, color: '#3730a3', fontSize: '14px' }}>👤 Admin view</span>
          {currentCust
            ? <span style={{ fontSize: '13px', color: '#475569', fontWeight: 600 }}>Viewing customer <b style={{ color: '#0f172a' }}>{currentCust.name}</b>{currentCust.account && <span style={{ color: '#94a3b8' }}> #{currentCust.account}</span>}</span>
            : <span style={{ fontSize: '13px', color: '#475569' }}>Pick a customer to view their app.</span>}
          {/* Direct customer switcher — opens the account picker on the "by
              account" tab so you can jump to ANY customer, not just another car
              in the one you're already viewing. */}
          <button
            onClick={() => { playOpen(); setPickerMode('account'); setPickerOpen(true); setExpandedAcct(currentCust ? currentCust.id : null); }}
            style={{ marginLeft: 'auto', background: 'linear-gradient(135deg,#4f46e5,#4338ca)', color: 'white', fontWeight: 800, fontSize: '12px', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer' }}>
            🔄 Switch customer
          </button>
        </div>
      )}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <TagAlongWordmark size={34} color={IS_NATIVE ? '#eaf2ff' : '#12324a'} pin="#f8703c" />
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#f8703c', fontStyle: 'italic', letterSpacing: '0.2px', marginTop: '3px' }}>{t('tagline')}</div>
          {(() => {
            // prefer the logged-in customer's own profile; fall back to admin's record
            const disp = me ? { name: me.name || '', account: myAttrs.account || '', phone: myAttrs.phone || '', photo: myAttrs.photo || '' } : currentCust;
            if (!disp) return <p style={{ marginTop: '2px' }}>{t('subtitle')}</p>;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                {(disp.photo || disp.avatar) ? (
                  <img onClick={() => setAvatarBig(true)} src={disp.photo || disp.avatar} alt={disp.name} title={t('clickToEnlarge')} style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #f8703c', cursor: 'zoom-in' }} />
                ) : (
                  <div onClick={() => setAvatarBig(true)} title={t('clickToEnlarge')} style={{ width: '42px', height: '42px', borderRadius: '50%', background: 'linear-gradient(135deg,#f8703c,#f4511e)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '16px', fontFamily: 'Poppins, sans-serif', border: '2px solid #ffd9c7', cursor: 'zoom-in' }}>
                    {(disp.name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <span style={{ fontSize: '19px', fontWeight: 800, color: '#f8703c', fontFamily: 'Poppins, sans-serif', lineHeight: 1 }}>{disp.name} {disp.account && <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8' }}>#{disp.account}</span>}</span>
                  {disp.phone && <a href={`tel:${disp.phone}`} style={{ fontSize: '13px', fontWeight: 700, color: '#1d4ed8', textDecoration: 'none' }}>📞 {disp.phone}</a>}
                  {me && <button onClick={openProfile} style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: '#0066ff', fontWeight: 700, fontSize: '12px', cursor: 'pointer', padding: 0, marginTop: '2px' }}>{lang === 'es' ? '⚙️ Mi perfil' : '⚙️ My profile'}</button>}
                </div>
              </div>
            );
          })()}
        </div>
        <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: '999px', overflow: 'hidden' }} title={t('language')}>
          {[['en', 'EN'], ['es', 'ES']].map(([v, label]) => (
            <button key={v} className={`seg-frost ${lang === v ? 'on' : ''}`} onClick={() => setLang(v)} style={{ background: lang === v ? 'linear-gradient(135deg,#0066ff,#0047d0)' : 'white', color: lang === v ? 'white' : '#475569', border: 'none', fontSize: '12px', fontWeight: 800, padding: '8px 12px', cursor: 'pointer' }}>{label}</button>
          ))}
        </div>
        {/* Admin only: jump into the full customer experience. Hidden for a real
            logged-in customer (their `me` isn't a preview stub). */}
        {!PREVIEW_AS && !(me && me.id !== 'preview') && (() => {
          const acct = (currentCust && currentCust.account) || (getCustomers()[0] && getCustomers()[0].account);
          if (!acct) return null;
          return <button onClick={() => { window.location.href = `/car?tagalong&as=${acct}`; }} title="Open the customer-facing view" style={{ background: '#f5f3ff', color: '#6d28d9', fontWeight: 700, fontSize: '13px', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '9px 14px', cursor: 'pointer' }}>👁 {lang === 'es' ? 'Ver como cliente' : 'View as customer'}</button>;
        })()}
        {/* Admin previewing as a customer: clear toggle back to the admin app. */}
        {PREVIEW_AS && (
          <button onClick={() => { window.location.href = '/car'; }} title="Leave the customer view and go back to admin" style={{ background: '#eef2ff', color: '#1d4ed8', fontWeight: 700, fontSize: '13px', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '9px 14px', cursor: 'pointer' }}>← {lang === 'es' ? 'Salir de vista cliente' : 'Exit customer view'}</button>
        )}
        {/* Customer's own sign out — always available in the customer experience
            (in admin preview it drops you at the real customer login). */}
        {me && (
          <button onClick={async () => { await logout(); window.location.href = '/car?tagalong'; }} title={lang === 'es' ? 'Cerrar sesión' : 'Sign out'} style={{ background: '#fef2f2', color: '#b91c1c', fontWeight: 700, fontSize: '13px', border: '1px solid #fecaca', borderRadius: '8px', padding: '9px 14px', cursor: 'pointer' }}>🚪 {lang === 'es' ? 'Cerrar sesión' : 'Sign out'}</button>
        )}
        <button onClick={() => device && setHistoryDevice(device)} style={{ background: '#0f172a', color: 'white', fontWeight: 700, fontSize: '13px', border: 'none', borderRadius: '8px', padding: '9px 14px', cursor: 'pointer' }}>{t('tripMap')}</button>
        <div style={{ position: 'relative' }}>
          <button onClick={() => { playOpen(); setPickerOpen((o) => !o); setExpandedAcct(currentCust ? currentCust.id : null); }}
            style={{ ...inp, width: 'auto', minWidth: '190px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left', fontWeight: 700 }}>
            <span style={{ flex: 1 }}>
              {device ? dName(device) : t('selectCar')}
              {currentCust && <span style={{ fontWeight: 600, color: TXT_MUTED, fontSize: '12px' }}> · #{currentCust.account}</span>}
            </span>
            <span style={{ color: '#94a3b8' }}>{pickerOpen ? '▲' : '▼'}</span>
          </button>
          {pickerOpen && (
            <>
              <div onClick={() => setPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1200 }} />
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '6px', width: 'min(300px, 84vw)', maxHeight: '420px', overflowY: 'auto', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 14px 34px rgba(15,23,42,0.20)', zIndex: 1201 }}>
                {/* mode toggle: by account vs all devices */}
                <div style={{ display: 'flex', gap: '4px', padding: '8px', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0, background: 'white' }}>
                  {[['account', t('byAccount')], ['all', t('allDevices')]].map(([v, label]) => (
                    <button key={v} className={`seg-frost ${pickerMode === v ? 'on' : ''}`} onClick={() => setPickerMode(v)} style={{ flex: 1, background: pickerMode === v ? 'linear-gradient(135deg,#0066ff,#0047d0)' : '#f1f5f9', color: pickerMode === v ? 'white' : '#475569', border: 'none', borderRadius: '8px', padding: '7px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>{label}</button>
                  ))}
                </div>

                {pickerMode === 'all' ? (
                  <div style={{ padding: '6px' }}>
                    {devices.map((d) => {
                      const on = String(d.id) === String(deviceId);
                      return (
                        <button key={d.id} onClick={() => pickDevice(d.id)} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', background: on ? '#eff6ff' : 'transparent', color: on ? '#1d4ed8' : '#0f172a', border: 'none', borderRadius: '8px', padding: '9px 10px', fontSize: '13px', fontWeight: on ? 800 : 600, cursor: 'pointer' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: d.status === 'online' ? '#22c55e' : '#cbd5e1' }} />
                          <span style={{ flex: 1 }}>{dName(d)}{(d.attributes || {}).displayName ? <span style={{ fontWeight: 600, color: '#94a3b8', fontSize: '11px' }}> · {d.name}</span> : null}</span>
                          {on && <span>✓</span>}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '6px' }}>
                    {/* search — every customer is listed (even ones with no car
                        yet), so a big roster is easy to find in. */}
                    <input
                      autoFocus
                      value={custPickSearch}
                      onChange={(e) => setCustPickSearch(e.target.value)}
                      placeholder="🔍 Search customer name or account #"
                      style={{ ...inp, marginBottom: '8px', padding: '8px 10px' }}
                    />
                    {(() => {
                      const q = custPickSearch.trim().toLowerCase();
                      const all = getCustomers().slice().sort((a, b) => String(a.account || '').localeCompare(String(b.account || '')));
                      const rows = all
                        .filter((c) => !q || `${c.name || ''} ${c.account || ''} ${c.phone || ''} ${c.email || ''}`.toLowerCase().includes(q))
                        .map((c) => ({ c, cars: custDeviceList(c) }));
                      // devices not owned by ANY customer → "Unassigned" bucket
                      const claimed = new Set();
                      all.forEach((c) => custDeviceList(c).forEach((d) => claimed.add(String(d.id))));
                      const unassigned = devices.filter((d) => !claimed.has(String(d.id)));
                      const showUnassigned = unassigned.length && (!q || t('unassigned').toLowerCase().includes(q) || 'unassigned'.includes(q));
                      if (!rows.length && !showUnassigned) {
                        return <div style={{ fontSize: '12px', color: '#94a3b8', padding: '10px', textAlign: 'center' }}>No customers match “{custPickSearch}”.</div>;
                      }
                      return (
                        <>
                          {rows.map(({ c, cars }) => {
                            const isCurrent = currentCust && String(currentCust.id) === String(c.id);
                            const has = cars.length > 0;
                            return (
                              <button key={c.id} disabled={!has} onClick={() => { if (has) { setDeviceId(String(cars[0].id)); setPickerOpen(false); setCustPickSearch(''); } }}
                                title={has ? 'Open this customer' : 'This customer has no car assigned yet'}
                                style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', background: isCurrent ? '#eff6ff' : (has ? '#f8fafc' : '#fff'), border: `1px solid ${isCurrent ? '#bfdbfe' : '#eef2f7'}`, borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: 700, color: isCurrent ? '#1d4ed8' : (has ? '#0f172a' : '#94a3b8'), cursor: has ? 'pointer' : 'default', opacity: has ? 1 : 0.7 }}>
                                <span style={{ flex: 1 }}>#{c.account} · {c.name}</span>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', background: 'white', border: '1px solid #e2e8f0', borderRadius: '999px', padding: '1px 8px' }}>{cars.length} {cars.length === 1 ? 'car' : 'cars'}</span>
                                {isCurrent && <span>✓</span>}
                              </button>
                            );
                          })}
                          {showUnassigned && (
                            <button onClick={() => { setDeviceId(String(unassigned[0].id)); setPickerOpen(false); setCustPickSearch(''); }}
                              style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: 700, color: '#92400e', cursor: 'pointer' }}>
                              <span style={{ flex: 1 }}>{t('unassigned')}</span>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', background: 'white', border: '1px solid #fde68a', borderRadius: '999px', padding: '1px 8px' }}>{unassigned.length} {unassigned.length === 1 ? 'car' : 'cars'}</span>
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {!restrictIds && <AddSharedVehicle onAdded={() => setSharedTick((n) => n + 1)} buttonLabel={t('addVehicle')} />}
      </div>

      {/* all cars on the selected account — click one to open its info below */}
      {accountDevices.length > 1 && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: TXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
            {currentCust ? `#${currentCust.account} · ${currentCust.name}` : t('thisAccount')} — {accountDevices.length} {t('cars')} · {t('tapToOpen')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {accountDevices.filter((d) => String(d.id) !== String(deviceId)).map((d) => {
              const p = positions.find((x) => String(x.deviceId) === String(d.id));
              const online = d.status === 'online';
              const dobd = p ? obdInfo(p.attributes) : {};
              return (
                <button key={d.id} onClick={() => pickDevice(d.id)} title={t('switchToCar')}
                  style={{ textAlign: 'left', width: '100%', display: 'flex', alignItems: 'center', gap: '10px', color: 'white', cursor: 'pointer',
                    background: '#1e293b', border: '2px solid transparent', borderRadius: '14px', padding: '12px 14px' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: online ? '#22c55e' : '#94a3b8', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', flex: 1 }}>{dName(d)}{(d.attributes || {}).displayName ? <span style={{ fontWeight: 600, color: '#94a3b8', fontSize: '11px' }}> · {d.name}</span> : null}</span>
                  {p && <span style={{ fontSize: '12px', color: '#cbd5e1' }}>🏁 {deghostMph(p)} mph</span>}
                  {dobd.fuelLevel != null && <span style={{ fontSize: '12px', color: '#cbd5e1' }}>⛽ {dobd.fuelLevel}%</span>}
                  <span style={{ fontSize: '12px', fontWeight: 700, color: online ? '#4ade80' : '#cbd5e1' }}>{online ? t('on') : t('off')}</span>
                  <span style={{ fontSize: '11px', color: TXT_MUTED, fontWeight: 700 }}>{t('open')}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* car profile — click the name to collapse/expand everything for THIS car */}
      <div style={{ ...card, background: on ? 'linear-gradient(135deg,#0a1a2f,#12325a)' : '#1e293b', color: 'white', marginBottom: '16px' }}>
        <div onClick={() => !renaming && toggleCollapse()} title={collapsed ? t('showThisCar') : t('hideThisCar')} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: collapsed ? 0 : '10px', cursor: renaming ? 'default' : 'pointer', userSelect: 'none' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: on ? '#22c55e' : '#94a3b8' }} />
          {renaming ? (
            <input autoFocus value={nameDraft} onClick={(e) => e.stopPropagation()} onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setRenaming(false); }} onBlur={saveName}
              style={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '2px 8px', width: '180px' }} />
          ) : (
            <>
              <span style={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>{device ? dName(device) : 'Car'}</span>
              <button onClick={(e) => { e.stopPropagation(); setNameDraft(device ? dName(device) : ''); setRenaming(true); }} title={t('rename')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}>✏️</button>
            </>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: 700, color: on ? '#4ade80' : '#cbd5e1' }}>{on ? t('on') : t('off')}</span>
          {!renaming && <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '10px' }}>{collapsed ? t('show') : t('hide')}</span>}
        </div>
        {!collapsed && (
          <>
            <div style={{ fontSize: '13px', color: '#cbd5e1', marginBottom: '12px' }}>{hasFix ? (addr || t('locating')) : t('waitingSignal')}</div>
            <div style={{ display: 'flex', gap: '8px', fontSize: '13px', flexWrap: 'wrap' }}>
              {(() => { const chip = { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '8px 12px', fontWeight: 600, color: '#fff' }; return <>
              <span style={chip}>🏁 {mph} mph</span>
              {obd.fuelLevel != null && <span style={chip}>⛽ {obd.fuelLevel}%</span>}
              {obd.power != null && <span style={chip}>🔋 {obd.power}V</span>}
              </>; })()}
            </div>
          </>
        )}
      </div>

      {collapsed && <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '-6px' }}>{device ? dName(device) : 'This car'} {t('collapsedNote')}</p>}

      {!collapsed && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', alignItems: 'start' }}>
        {/* stats + places */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* bento row: big miles tile + driver-score ring (native only) */}
          {IS_NATIVE && (() => {
            const rough = stats ? (stats.brakes || 0) + (stats.accel || 0) + (stats.speeding || 0) : 0;
            const score = Math.max(40, Math.min(100, 100 - rough * 6));
            const ring = score >= 85 ? '#22c55e' : score >= 70 ? '#fbbf24' : '#f87171';
            return (
              <div style={{ display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div style={{ ...card, borderRadius: '22px', background: 'linear-gradient(150deg, rgba(124,58,237,0.32), rgba(59,130,246,0.12) 60%, rgba(255,255,255,0.05))', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={lbl}>{t('milesToday')}</div>
                  <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '38px', letterSpacing: '-1px', lineHeight: 1, marginTop: '6px', color: '#fff' }}>{stats ? stats.miles.toFixed(1) : '—'}</div>
                  <div style={{ fontSize: '12px', color: TXT_MUTED, marginTop: '6px' }}>{stats ? `${stats.trips} ${t('tripsTopSpeed')} ${stats.topSpeed} mph` : '—'}</div>
                </div>
                <div style={{ ...card, borderRadius: '22px', background: 'linear-gradient(150deg, rgba(34,197,94,0.26), rgba(16,185,129,0.06))', border: '1px solid rgba(34,197,94,0.25)', textAlign: 'center' }}>
                  <div style={{ width: '76px', height: '76px', borderRadius: '50%', margin: '2px auto 10px', background: `conic-gradient(${ring} 0% ${score}%, rgba(255,255,255,0.10) ${score}% 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#0d1522', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '21px', color: '#fff' }}>{score}</span>
                  </div>
                  <div style={{ ...lbl, color: '#86efac' }}>{t('driverScore')}</div>
                  <div style={{ fontSize: '11px', color: '#bbf7d0', marginTop: '2px' }}>{t('today')}</div>
                </div>
              </div>
            );
          })()}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            {!IS_NATIVE && st(t('milesToday'), stats ? stats.miles.toFixed(1) : '—')}
            {!IS_NATIVE && st(t('topSpeed'), stats ? `${stats.topSpeed}` : '—', stats && stats.topSpeed > 80 ? '#dc2626' : '#0f172a')}
            {!IS_NATIVE && st(t('trips'), stats ? stats.trips : '—')}
            {stTap('brakes', t('hardBrakes'), stats ? stats.brakeEvents : null, stats && stats.brakes > 0 ? '#b45309' : '#0f172a')}
            {stTap('accel', t('hardAccel'), stats ? stats.accelEvents : null, stats && stats.accel > 0 ? '#b45309' : '#0f172a')}
            {stTap('speeding', t('speeding'), stats ? stats.speedEvents : null, stats && stats.speeding > 0 ? '#dc2626' : '#0f172a')}
          </div>

          {/* expanded list for whichever driver-score card was tapped */}
          {openStat && stats && (() => {
            const map = {
              brakes: { label: t('hardBrakes'), icon: '🛑', events: stats.brakeEvents },
              accel: { label: t('hardAccel'), icon: '💨', events: stats.accelEvents },
              speeding: { label: t('speeding'), icon: '🚧', events: stats.speedEvents },
            };
            const cur = map[openStat];
            if (!cur) return null;
            return (
              <div style={{ ...card, ...(IS_NATIVE ? { background: 'linear-gradient(160deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))' } : {}), padding: '10px 12px', borderRadius: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: TXT, marginBottom: '6px' }}>{cur.icon} {cur.label} — {t('today')}</div>
                {(!cur.events || cur.events.length === 0) && <div style={{ fontSize: '12px', color: TXT_MUTED }}>None recorded today.</div>}
                {(cur.events || []).map((evt, i) => (
                  <div key={i} onClick={() => { playOpen(); setStatDetail({ icon: cur.icon, title: evt.label, entity: device ? dName(device) : '', place: evt.place }); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 4px', borderTop: i ? `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` : 'none', fontSize: '13px', cursor: 'pointer' }}>
                    <span style={{ fontSize: '12px', color: TXT_MUTED, width: '64px', flexShrink: 0 }}>{evt.t ? new Date(evt.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    <span style={{ flex: 1, color: TXT }}>{evt.label}{evt.place && <span style={{ color: '#60a5fa', fontWeight: 700 }}> · 📍</span>}</span>
                    <span style={{ color: TXT_MUTED, fontSize: '15px' }}>›</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* places / arrivals */}
          <div style={{ ...card, ...tint('#eff6ff', '1px solid #dbeafe') }}>
            <div style={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', marginBottom: '8px' }}>{t('placesTitle')}</div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <input style={{ ...inp, flex: '1 1 90px', minWidth: 0 }} placeholder={t('placeName')} value={placeName} onChange={(e) => setPlaceName(e.target.value)} />
              <AddressAutocomplete
                style={{ ...inp, flex: '2 1 140px', minWidth: 0 }}
                placeholder={t('address')}
                value={placeAddr}
                onChange={(v) => { setPlaceAddr(v); setPlaceAddrPt(null); }}
                onPick={(p) => { setPlaceAddr(p.description); setPlaceAddrPt(p.lat != null ? { lat: p.lat, lng: p.lng } : null); }}
                onEnter={addPlace} />
              <button onClick={addPlace} style={{ background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 700, border: 'none', borderRadius: '8px', padding: '0 14px', cursor: 'pointer' }}>{t('add')}</button>
            </div>
            {/* make the two tiers obvious: per-car here vs. all-cars on the Vehicles tab */}
            <div style={{ fontSize: '11.5px', lineHeight: 1.5, color: IS_NATIVE ? '#93c5fd' : '#1d4ed8', background: IS_NATIVE ? 'rgba(59,130,246,0.10)' : '#eff6ff', border: `1px solid ${IS_NATIVE ? 'rgba(59,130,246,0.25)' : '#dbeafe'}`, borderRadius: '10px', padding: '8px 10px', margin: '0 0 10px' }}>
              {t('placesScopeHint')}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {myPlaces.map((p, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, borderRadius: '999px', padding: '3px 6px 3px 10px' }}>
                  ⭐ {p.name}
                  <button onClick={() => removePlace(p)} title={t('removePlace')} style={{ background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0, lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>
            <div style={lbl}>{t('recentArrivals')}</div>
            {events.length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{t('noneRecent')}</div>}
            {events.map((e, i) => {
              const gf = geofences.find((g) => g.id === e.geofenceId);
              const placeNm = gf ? (gf.name || '').replace(/^[⭐⏱]\s*/, '').trim() : '';
              const enter = e.type === 'geofenceEnter';
              const verb = enter ? (lang === 'es' ? '➡️ Llegó a' : '➡️ Arrived at') : (lang === 'es' ? '⬅️ Salió de' : '⬅️ Left');
              const where = placeNm || (lang === 'es' ? 'un lugar guardado' : 'a saved place');
              return (
                <div key={i} style={{ fontSize: '12.5px', color: TXT, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                  {verb} <strong>{where}</strong> · {new Date(e.eventTime).toLocaleString()}
                </div>
              );
            })}
          </div>

          {/* Smart curfew — direction-aware */}
          {curfew && (
          <div style={{ ...card, ...tint('#faf5ff', '1px solid #e9d5ff') }}>
            <div onClick={() => setCurfewOpen((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', flex: 1 }}>{t('curfewTitle')} {curfew.enabled ? <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700 }}>· on ({curfew.startTime}–{curfew.endTime})</span> : <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700 }}>· off</span>}</span>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>{curfewOpen ? '▾' : '▸'}</span>
            </div>
            {curfewOpen && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '11px', color: '#7c3aed', background: '#f5f3ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '8px', marginBottom: '10px' }}>
                  After curfew, if the car is <b>heading home</b> you get a calm “heading home — ETA 11:15”. If it’s <b>out and moving away</b>, you get a real alert. Direction matters.
                </div>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', marginBottom: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!curfew.enabled} onChange={(e) => setCf({ enabled: e.target.checked })} /> {t('curfewEnable')}
                </label>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...lbl, marginBottom: '5px' }}>{t('curfewFrom')}</div>
                    <input type="time" style={{ ...inp, height: '44px', fontSize: '16px', textAlign: 'center', WebkitAppearance: 'none' }} value={curfew.startTime} onChange={(e) => setCf({ startTime: e.target.value })} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...lbl, marginBottom: '5px' }}>{t('curfewUntil')}</div>
                    <input type="time" style={{ ...inp, height: '44px', fontSize: '16px', textAlign: 'center', WebkitAppearance: 'none' }} value={curfew.endTime} onChange={(e) => setCf({ endTime: e.target.value })} />
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ ...lbl, marginBottom: '7px' }}>{t('curfewDays')}</div>
                  <div style={{ display: 'flex', gap: '7px' }}>
                    {[['Su', 0], ['Mo', 1], ['Tu', 2], ['We', 3], ['Th', 4], ['Fr', 5], ['Sa', 6]].map(([lab, d]) => {
                      const on = (curfew.days || []).includes(d);
                      return (
                        <button key={d} type="button" onClick={() => { const cur = curfew.days || []; setCf({ days: on ? cur.filter((x) => x !== d) : [...cur, d] }); }}
                          style={{ flex: '1 1 0', minWidth: 0, height: '40px', borderRadius: '10px', border: '1px solid ' + (on ? 'transparent' : '#e9d5ff'), background: on ? 'linear-gradient(135deg,#7c3aed,#6d28d9)' : 'white', color: on ? 'white' : '#7c3aed', fontWeight: 700, fontSize: '12px', cursor: 'pointer', padding: 0 }}>
                          {lab}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <span style={lbl}>{t('curfewHome')}</span>
                  {myPlaces.length ? (
                    <select style={inp} value={curfew.homeName || ''} onChange={(e) => { const pl = myPlaces.find((x) => x.name === e.target.value); if (pl) setCf({ homeName: pl.name, homeLat: pl.lat, homeLng: pl.lng }); }}>
                      <option value="">{t('curfewChooseHome')}</option>
                      {myPlaces.map((pl, i) => <option key={i} value={pl.name}>⭐ {pl.name}</option>)}
                    </select>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#b45309' }}>Add a saved place above first (e.g. “Home”), then pick it here.</div>
                  )}
                  {curfew.homeName && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px' }}>✓ Home set to “{curfew.homeName}”</div>}
                </div>
                <div style={{ textAlign: 'right' }}><button onClick={saveCurfewCfg} style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: 'white', fontWeight: 700, fontSize: '12px', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer' }}>{t('curfewSave')}</button></div>
              </div>
            )}
          </div>
          )}

          {/* Speed-limit-aware alert — per car, off by default so it doesn't nag
              every vehicle. The regular speeding/overspeed alerts stay as-is. */}
          {device && (
          <div style={{ ...card, ...tint('#fff7ed', '1px solid #fed7aa') }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>{t('spdTitle')} <span style={{ fontSize: '11px', color: speedLimitOn ? '#16a34a' : '#94a3b8', fontWeight: 700 }}>· {speedLimitOn ? 'on' : 'off'}</span></div>
                <div style={{ fontSize: '12px', color: '#9a3412', marginTop: '4px' }}>Alerts when this car goes more than <b>7 mph over the posted limit</b> on the road it's on (e.g. “55 in a 35”), not just a fixed number. Where a street has no posted limit on record, the typical limit for that road type is used. Your regular speeding alert stays on.</div>
              </div>
              <button type="button" disabled={savingSpd} onClick={() => toggleSpeedLimit(!speedLimitOn)}
                style={{ flexShrink: 0, width: '52px', height: '30px', minWidth: '52px', borderRadius: '999px', border: 'none', padding: 0, boxSizing: 'border-box', WebkitAppearance: 'none', appearance: 'none', cursor: savingSpd ? 'default' : 'pointer', background: speedLimitOn ? 'linear-gradient(135deg,#f97316,#ea580c)' : '#cbd5e1', position: 'relative', transition: 'background .15s' }}>
                <span style={{ position: 'absolute', top: '3px', left: speedLimitOn ? '25px' : '3px', width: '24px', height: '24px', borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,.3)', transition: 'left .15s' }} />
              </button>
            </div>
          </div>
          )}


          {/* Alerts — see all set alerts + create your own, with per-alert delivery */}
          <div style={{ ...card, ...tint('#f0fdf4', '1px solid #bbf7d0') }}>
            <div onClick={() => setAlertsOpen((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', flex: 1 }}>🔔 Alerts</span>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>{alertsOpen ? '▾' : '▸'}</span>
            </div>
            {alertsOpen && alerts && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '11px', color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px', marginBottom: '10px' }}>🔔 Alerts are delivered <b>in the app</b> (banner + spoken + notifications) — free, no phone plan needed. Text & call are available for later once telephony is connected.</div>

                <span style={lbl}>{t('builtInAlerts')}</span>
                {BUILTIN_ALERTS.map((a) => {
                  const b = alerts.builtins[a.key] || { on: true, channel: 'app' };
                  const high = HIGH_PRIORITY.includes(a.priority);
                  return (
                    <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '44px', padding: '6px 0', borderBottom: `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
                      <input type="checkbox" checked={!!b.on} onChange={(e) => setBuiltin(a.key, { on: e.target.checked })} style={{ width: '18px', height: '18px', flexShrink: 0, accentColor: '#0066ff' }} />
                      <span style={{ fontSize: '12.5px', flex: 1, minWidth: 0, color: TXT, lineHeight: 1.35 }}>
                        {a.label}
                        {high && <span style={{ fontSize: '9px', fontWeight: 800, color: '#fca5a5', background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px', padding: '1px 5px', marginLeft: '5px', whiteSpace: 'nowrap' }}>{a.priority.toUpperCase()}</span>}
                      </span>
                      {a.threshold && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          <input type="number" value={thVal(a.threshold.attr)} placeholder={a.threshold.def} onChange={(e) => setThresholds((t) => ({ ...t, [a.threshold.attr]: e.target.value }))} title={`Set the ${a.threshold.unit} threshold`} style={{ ...inp, width: '62px', padding: '6px', textAlign: 'center' }} />
                          <span style={{ fontSize: '10px', color: TXT_MUTED }}>{a.threshold.unit}</span>
                        </span>
                      )}
                      {chanToggle()}
                    </div>
                  );
                })}

                <div style={{ marginTop: '12px' }}><span style={lbl}>{t('yourCustomAlerts')}</span>
                  {alerts.custom.length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>None yet — build one below.</div>}
                  {alerts.custom.map((c) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <input type="checkbox" checked={!!c.on} onChange={(e) => setCustom(c.id, { on: e.target.checked })} />
                      <span style={{ fontSize: '12.5px', flex: 1, color: TXT }}>{c.name}: {metricOf(c.metric).label} {c.op} {c.value}{metricOf(c.metric).unit}</span>
                      {chanToggle()}
                      <button onClick={() => removeCustom(c.id)} style={{ background: '#fef2f2', color: '#b91c1c', border: 'none', borderRadius: '6px', width: '24px', cursor: 'pointer', fontWeight: 700 }}>×</button>
                    </div>
                  ))}
                </div>

                {(() => {
                  const fieldWrap = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 };
                  const fieldLbl = { fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: TXT_MUTED };
                  const field = { ...inp, width: '100%', boxSizing: 'border-box', height: '40px', padding: '0 10px' };
                  return (
                    <div style={{ marginTop: '12px', border: `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.14)' : '#cbd5e1'}`, borderRadius: '14px', padding: '14px', background: IS_NATIVE ? 'rgba(255,255,255,0.05)' : '#f8fafc' }}>
                      <div style={{ ...lbl, marginBottom: '10px' }}>{t('createAlert')}</div>
                      <div style={{ ...fieldWrap, marginBottom: '10px' }}>
                        <span style={fieldLbl}>{t('alertNameIt')}</span>
                        <input style={field} placeholder={t('alertNamePh')} value={newAlert.name} onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.9fr', gap: '8px', marginBottom: '12px' }}>
                        <div style={fieldWrap}>
                          <span style={fieldLbl}>{t('alertWhen')}</span>
                          <select style={field} value={newAlert.metric} onChange={(e) => setNewAlert({ ...newAlert, metric: e.target.value })}>{CUSTOM_METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}</select>
                        </div>
                        <div style={fieldWrap}>
                          <span style={fieldLbl}>{t('alertIs')}</span>
                          <select style={field} value={newAlert.op} onChange={(e) => setNewAlert({ ...newAlert, op: e.target.value })}><option value="over">{t('alertOver')}</option><option value="under">{t('alertUnder')}</option></select>
                        </div>
                        <div style={fieldWrap}>
                          <span style={fieldLbl}>{t('alertValue')}</span>
                          <input type="number" style={{ ...field, textAlign: 'center' }} placeholder={metricOf(newAlert.metric).unit || '0'} value={newAlert.value} onChange={(e) => setNewAlert({ ...newAlert, value: e.target.value })} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {chanToggle()}
                        <button onClick={addCustomAlert} style={{ marginLeft: 'auto', background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontSize: '13px', fontWeight: 700, border: 'none', borderRadius: '10px', padding: '10px 18px', cursor: 'pointer' }}>{t('addAlert')}</button>
                      </div>
                    </div>
                  );
                })()}

                <div style={{ textAlign: 'right', marginTop: '10px' }}><button onClick={saveAlerts} style={{ background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 700, fontSize: '12px', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer' }}>{t('saveAlerts')}</button></div>
                <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '8px' }}>In-app delivery is on now (banner, voice & notifications). Text and call activate later when telephony is connected.</p>
              </div>
            )}
          </div>
        </div>

        {/* ask the car — Talk membership only */}
        {!talkAllowed ? (
          <div style={{ ...card, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', minHeight: '260px', background: 'linear-gradient(160deg,#faf5ff,#eef2ff)', border: '1px solid #e9d5ff' }}>
            <div style={{ fontSize: '34px' }}>💬</div>
            <div style={{ fontWeight: 800, fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: TXT, margin: '8px 0 4px' }}>{lang === 'es' ? 'Habla con tu auto' : 'Talk to your car'}</div>
            <p style={{ fontSize: '13px', color: TXT_MUTED, maxWidth: '300px', margin: '0 0 14px' }}>
              {lang === 'es'
                ? 'Pregúntale a tu auto por texto o llamada — dónde está, cómo maneja, gasolina y más. Disponible con la membresía Talk.'
                : 'Ask your car anything by text or call — where it is, how it drives, fuel and more. Included with the Talk membership.'}
            </p>
            <a href={`mailto:support@mytagalong.app?subject=${encodeURIComponent(lang === 'es' ? 'Mejorar a la membresía Talk' : 'Upgrade to Talk membership')}`} style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: 'white', fontWeight: 800, fontSize: '14px', border: 'none', borderRadius: '10px', padding: '11px 20px', cursor: 'pointer', textDecoration: 'none' }}>
              {lang === 'es' ? 'Mejorar a Talk' : 'Upgrade to Talk'}
            </a>
          </div>
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', height: '560px', borderRadius: '20px', overflow: 'hidden', background: IS_NATIVE ? 'rgba(255,255,255,0.05)' : '#ffffff', border: IS_NATIVE ? '1px solid rgba(255,255,255,0.10)' : '1px solid #e2e8f0', boxShadow: IS_NATIVE ? 'inset 0 1px 0 rgba(255,255,255,0.07)' : '0 6px 22px rgba(15,23,42,0.10)' }}>
          {/* iMessage-style header bar — makes it obvious this is a texting screen to reach the car */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', background: IS_NATIVE ? 'rgba(255,255,255,0.06)' : 'linear-gradient(180deg,#f7f8fa,#eef0f4)', borderBottom: IS_NATIVE ? '1px solid rgba(255,255,255,0.10)' : '1px solid #e2e8f0' }}>
            {carPhoto
              ? <img src={carPhoto} alt="" style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, boxShadow: '0 2px 6px rgba(10,132,255,0.35)', border: '2px solid #cfe4ff' }} />
              : <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg,#0a84ff,#0060df)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px', flexShrink: 0, boxShadow: '0 2px 6px rgba(10,132,255,0.35)' }}>🚗</div>}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', fontSize: '15px', color: TXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(device && dName(device)) || 'TagAlong'}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: '#22c55e', fontWeight: 600 }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                {lang === 'es' ? 'Escríbele a tu auto · responde en la app' : 'Text your car · replies in-app'}
              </div>
            </div>
          </div>

          {/* message thread */}
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px', padding: '14px 12px', background: IS_NATIVE ? 'transparent' : '#ffffff' }}>
            {chat.map((m, i) => {
              const mine = m.from === 'me';
              return (
                <div key={i} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '80%', display: 'flex', flexDirection: 'column', gap: '6px', alignItems: mine ? 'flex-end' : 'flex-start', margin: '5px 0' }}>
                  <div style={{
                    background: mine ? 'linear-gradient(135deg,#28a0ff,#0a84ff)' : '#e9e9eb',
                    color: mine ? '#fff' : '#0f172a',
                    padding: '9px 14px', fontSize: '14px', lineHeight: 1.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    borderRadius: '19px',
                    borderBottomRightRadius: mine ? '5px' : '19px',
                    borderBottomLeftRadius: mine ? '19px' : '5px',
                    boxShadow: '0 1px 1px rgba(15,23,42,0.08)',
                  }}>{linkify(m.text, mine)}</div>
                  {m.openRentals && (
                    <button onClick={() => { window.location.href = `/rentals${window.location.search || ''}`; }} style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: 'white', fontWeight: 700, fontSize: '12px', border: 'none', borderRadius: '999px', padding: '5px 12px', cursor: 'pointer' }}>📄 Open Rentals (view contract)</button>
                  )}
                  {m.mapDevice && (
                    <button onClick={() => setHistoryDevice(m.mapDevice)} style={{ background: '#0f172a', color: 'white', fontWeight: 700, fontSize: '12px', border: 'none', borderRadius: '999px', padding: '5px 12px', cursor: 'pointer' }}>{t('seeOnMap')}</button>
                  )}
                </div>
              );
            })}
            {thinking && (
              <div style={{ alignSelf: 'flex-start', background: '#e9e9eb', color: '#8a8a8e', borderRadius: '19px', borderBottomLeftRadius: '5px', padding: '11px 15px', fontSize: '14px', margin: '5px 0', letterSpacing: '2px' }}>· · ·</div>
            )}
            <div ref={chatEnd} />
          </div>

          {/* quick prompts — collapse to a small toggle once the chat has started */}
          {showSuggest ? (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', padding: '8px 12px 2px', overflowX: 'auto' }}>
              {getSuggested(lang).slice(0, 5).map((s) => <button key={s} onClick={() => { playOpen(); ask(s); }} style={{ fontSize: '11.5px', background: '#eef4ff', color: '#0a84ff', fontWeight: 600, border: '1px solid #d6e4ff', borderRadius: '999px', padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>{s}</button>)}
            </div>
          ) : (
            <div style={{ padding: '6px 12px 2px' }}>
              <button onClick={() => { playOpen(); setShowSuggest(true); }} style={{ fontSize: '11.5px', background: '#f1f5f9', color: '#475569', fontWeight: 700, border: '1px solid #e2e8f0', borderRadius: '999px', padding: '6px 12px', cursor: 'pointer' }}>💡 {lang === 'es' ? 'Sugerencias' : 'Suggestions'} ▾</button>
            </div>
          )}

          {/* iMessage-style composer: pill field + round blue send */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '10px 12px 12px', borderTop: `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.10)' : '#eef0f4'}`, background: IS_NATIVE ? 'rgba(255,255,255,0.04)' : '#ffffff' }}>
            <input
              style={{ flex: 1, padding: '11px 16px', border: IS_NATIVE ? '1px solid rgba(255,255,255,0.16)' : '1px solid #d1d5db', borderRadius: '999px', fontSize: '15px', boxSizing: 'border-box', background: IS_NATIVE ? 'rgba(255,255,255,0.08)' : '#fff', color: TXT, outline: 'none' }}
              placeholder={lang === 'es' ? 'Mensaje a tu auto…' : 'Message your car…'}
              value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') ask(); }} />
            <button onClick={() => ask()} disabled={!q.trim()} aria-label={t('askButton')} style={{ width: '40px', height: '40px', flexShrink: 0, background: q.trim() ? 'linear-gradient(135deg,#28a0ff,#0a84ff)' : '#cbd5e1', color: 'white', fontWeight: 700, fontSize: '19px', border: 'none', borderRadius: '50%', cursor: q.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>↑</button>
          </div>
        </div>
        )}
      </div>
      )}

      <p style={{ fontSize: '11px', color: TXT_MUTED, marginTop: '14px' }}>{t('footer')}</p>

      {/* company contact — always reachable at the bottom of the page */}
      <div style={{
        marginTop: '14px', marginBottom: '10px', textAlign: 'center',
        background: IS_NATIVE ? 'rgba(255,255,255,0.05)' : '#f8fafc',
        border: `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
        borderRadius: '16px', padding: '16px 14px',
      }}>
        <div style={{ fontSize: '12.5px', color: TXT_MUTED, marginBottom: '8px' }}>
          {lang === 'es' ? '¿Necesitas ayuda con tu auto o tu cuenta?' : 'Need help with your car or your account?'}
        </div>
        <a href="tel:+13055040711" style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px', textDecoration: 'none',
          background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: 'white',
          fontWeight: 800, fontSize: '15px', borderRadius: '999px', padding: '11px 22px',
        }}>📞 (305) 504-0711</a>
        <div style={{ fontSize: '11px', color: TXT_MUTED, marginTop: '8px' }}>
          {lang === 'es' ? 'Toca para llamar · Soporte TagAlong' : 'Tap to call · TagAlong support'}
        </div>
      </div>

      {/* My Profile — the customer edits their own personal info & billing */}
      {profileOpen && pForm && (
        <div onClick={() => setProfileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.6)', zIndex: 1450, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: 'min(460px, 96vw)', boxSizing: 'border-box', padding: '22px', margin: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
              <h2 style={{ margin: 0, flex: 1, fontFamily: 'Poppins, sans-serif', fontSize: '20px' }}>{lang === 'es' ? 'Mi perfil' : 'My profile'}</h2>
              <button onClick={() => setProfileOpen(false)} style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
            {(() => {
              const acct = (me && me.attributes && (me.attributes.account || me.attributes.acct)) || '';
              return acct ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', background: 'linear-gradient(135deg,#eff6ff,#e0e7ff)', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '10px 13px', marginBottom: '14px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e3a8a' }}>{lang === 'es' ? 'Número de cuenta' : 'Account number'}</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '16px', color: '#0f172a', letterSpacing: '0.5px' }}>#{acct}</span>
                </div>
              ) : null;
            })()}
            <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '14px' }}>
              <PhotoPicker facing="user" onPhoto={(url) => setPForm((p) => ({ ...p, photo: url }))} labels={lang === 'es' ? { take: '📷 Tomar foto', choose: '🖼️ Elegir de la galería', capture: 'Capturar', cancel: 'Cancelar', retake: 'Repetir', use: 'Usar foto' } : {}}>
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  {pForm.photo ? <img src={pForm.photo} alt="" style={{ width: '68px', height: '68px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #f8703c' }} />
                    : <div style={{ width: '68px', height: '68px', borderRadius: '50%', background: 'linear-gradient(135deg,#f8703c,#f4511e)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '22px' }}>{(pForm.name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}</div>}
                  <span style={{ position: 'absolute', bottom: 0, right: 0, background: '#0f172a', color: 'white', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>✎</span>
                </span>
              </PhotoPicker>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{lang === 'es' ? 'Toca la foto para tomar una nueva o elegir de la galería' : 'Tap the photo to take a new one or choose from library'}</div>
            </div>
            {[['name', lang === 'es' ? 'Nombre' : 'Name'], ['phone', lang === 'es' ? 'Teléfono' : 'Phone'], ['email', 'Email']].map(([k, label]) => (
              <div key={k} style={{ marginBottom: '10px' }}>
                <span style={{ ...lblLight }}>{label}</span>
                <input style={inpLight} value={pForm[k]} onChange={(e) => setPForm((p) => ({ ...p, [k]: e.target.value }))} />
              </div>
            ))}
            <div style={{ marginBottom: '10px' }}><span style={lblLight}>{lang === 'es' ? 'Idioma' : 'Language'}</span>
              <select style={inpLight} value={pForm.lang} onChange={(e) => setPForm((p) => ({ ...p, lang: e.target.value }))}><option value="en">English</option><option value="es">Español</option></select>
            </div>
            <div style={{ background: '#f6f9fc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
              <span style={lblLight}>{lang === 'es' ? 'Facturación' : 'Billing'}</span>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>{lang === 'es' ? 'Plan actual' : 'Current plan'}: <b style={{ color: '#0f172a' }}>{plan === 'talk' ? (lang === 'es' ? 'Talk (mensual)' : 'Talk (monthly)') : (lang === 'es' ? 'Track (anual)' : 'Track (yearly)')}</b></div>
              <span style={lblLight}>{lang === 'es' ? 'Tarjeta (últimos 4)' : 'Card (last 4)'}</span>
              <input style={{ ...inpLight, marginBottom: '8px' }} value={pForm.cardLast4} onChange={(e) => setPForm((p) => ({ ...p, cardLast4: e.target.value.replace(/\D/g, '').slice(0, 4) }))} placeholder="4242" />
              <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px', cursor: 'pointer' }}><input type="checkbox" checked={!!pForm.autopay} onChange={(e) => setPForm((p) => ({ ...p, autopay: e.target.checked }))} /> {lang === 'es' ? 'Pago automático' : 'Autopay'}</label>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>{lang === 'es' ? 'Para cambiar de plan o método de pago, contacta a soporte.' : 'To change plans or payment method, contact support.'}</div>
            </div>
            {/* Devices on this account — read-only list the customer can jump to */}
            <div style={{ background: '#f6f9fc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
              <span style={lblLight}>{lang === 'es' ? 'Mis dispositivos' : 'My devices'}</span>
              {devices.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>{lang === 'es' ? 'Aún no hay dispositivos asignados a tu cuenta.' : 'No devices are assigned to your account yet.'}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                  {devices.map((d) => (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 10px' }}>
                      <span style={{ fontSize: '16px' }}>🚗</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>{dName(d)}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>#{(d.attributes || {}).account || ''} · …{(d.uniqueId || '').slice(-6)}</div>
                      </div>
                      <button onClick={() => { pickDevice(d.id); setProfileOpen(false); }} style={{ background: '#eef2ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>{lang === 'es' ? 'Seguir' : 'Follow'}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setProfileOpen(false)} style={{ background: '#e2e8f0', color: '#334155', fontWeight: 600, border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</button>
              <button onClick={saveProfile} style={{ flex: 1, background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 700, border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>{lang === 'es' ? '💾 Guardar' : '💾 Save'}</button>
            </div>
            {/* Danger zone — permanent account deletion (App Store requirement) */}
            <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px solid #fee2e2' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#b91c1c', letterSpacing: '0.4px', marginBottom: '6px' }}>{lang === 'es' ? 'ZONA DE PELIGRO' : 'DANGER ZONE'}</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>{lang === 'es' ? 'Elimina tu cuenta y tus datos personales de forma permanente. Esta acción no se puede deshacer.' : 'Permanently delete your account and personal data. This cannot be undone.'}</div>
              <button onClick={() => { setDelText(''); setDelOpen(true); }} style={{ background: '#fef2f2', color: '#b91c1c', fontWeight: 700, fontSize: '13px', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer', width: '100%' }}>🗑️ {lang === 'es' ? 'Eliminar mi cuenta' : 'Delete my account'}</button>
            </div>
          </div>
        </div>
      )}
      {/* Account-deletion confirmation */}
      {delOpen && (
        <div onClick={() => !delBusy && setDelOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.7)', zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: 'min(420px, 96vw)', boxSizing: 'border-box', padding: '22px' }}>
            <h2 style={{ margin: '0 0 10px', fontFamily: 'Poppins, sans-serif', fontSize: '19px', color: '#b91c1c' }}>{lang === 'es' ? '¿Eliminar tu cuenta?' : 'Delete your account?'}</h2>
            <div style={{ fontSize: '13px', color: '#334155', lineHeight: 1.5, marginBottom: '14px' }}>
              {lang === 'es'
                ? 'Esto elimina de forma permanente tu inicio de sesión, tu perfil, tus lugares guardados y tus alertas. No se puede deshacer. Para confirmar, escribe '
                : 'This permanently deletes your login, profile, saved places, and alerts. This cannot be undone. To confirm, type '}
              <b>{lang === 'es' ? 'ELIMINAR' : 'DELETE'}</b>{lang === 'es' ? ' abajo.' : ' below.'}
            </div>
            <input autoFocus value={delText} onChange={(e) => setDelText(e.target.value)} placeholder={lang === 'es' ? 'ELIMINAR' : 'DELETE'} style={{ ...inpLight, marginBottom: '14px', textAlign: 'center', letterSpacing: '1px', fontWeight: 700 }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button disabled={delBusy} onClick={() => setDelOpen(false)} style={{ flex: 1, background: '#e2e8f0', color: '#334155', fontWeight: 600, border: 'none', borderRadius: '8px', padding: '11px 16px', cursor: 'pointer' }}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</button>
              <button disabled={delBusy || delText.trim().toUpperCase() !== (lang === 'es' ? 'ELIMINAR' : 'DELETE')} onClick={doDeleteAccount} style={{ flex: 1, background: (delBusy || delText.trim().toUpperCase() !== (lang === 'es' ? 'ELIMINAR' : 'DELETE')) ? '#fca5a5' : 'linear-gradient(135deg,#dc2626,#b91c1c)', color: 'white', fontWeight: 700, border: 'none', borderRadius: '8px', padding: '11px 16px', cursor: (delBusy || delText.trim().toUpperCase() !== (lang === 'es' ? 'ELIMINAR' : 'DELETE')) ? 'not-allowed' : 'pointer' }}>{delBusy ? (lang === 'es' ? 'Eliminando…' : 'Deleting…') : (lang === 'es' ? 'Eliminar cuenta' : 'Delete account')}</button>
            </div>
          </div>
        </div>
      )}

      {/* profile picture enlarged — click anywhere to shrink back */}
      {avatarBig && (currentCust || me) && (() => {
        const disp = me ? { name: me.name || '', account: myAttrs.account || '', photo: myAttrs.photo || '' } : currentCust;
        return (
        <div onClick={() => setAvatarBig(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.75)', zIndex: 1500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', cursor: 'zoom-out' }}>
          {(disp.photo || disp.avatar) ? (
            <img src={disp.photo || disp.avatar} alt={disp.name} style={{ width: 'min(320px, 80vw)', height: 'min(320px, 80vw)', borderRadius: '50%', objectFit: 'cover', border: '4px solid #f8703c', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }} />
          ) : (
            <div style={{ width: 'min(280px, 72vw)', height: 'min(280px, 72vw)', borderRadius: '50%', background: 'linear-gradient(135deg,#f8703c,#f4511e)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '96px', fontFamily: 'Poppins, sans-serif', border: '4px solid #ffd9c7', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
              {(disp.name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
            </div>
          )}
          <div style={{ color: 'white', fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '22px' }}>{disp.name} {disp.account && <span style={{ fontSize: '14px', color: '#cbd5e1' }}>#{disp.account}</span>}</div>
          <div style={{ color: '#94a3b8', fontSize: '12px' }}>{t('clickToClose')}</div>
        </div>
        );
      })()}

      {/* in-app trip map: route + ignition on/off + alert pins (street view on click) */}
      {historyDevice && <VehicleHistory device={historyDevice} devices={devices} onClose={() => setHistoryDevice(null)} />}
      {statDetail && <AlertDetailModal alert={statDetail} onClose={() => setStatDetail(null)} />}

      {/* add/remove the place on other vehicles */}
      {devModal && (
        <div onClick={() => setDevModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(420px, 96vw)', boxSizing: 'border-box', padding: '20px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>{devModal.mode === 'add' ? '➕' : '🗑️'} “{devModal.place.name}”</h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 12px' }}>
              {devModal.mode === 'add'
                ? 'You have more than one vehicle. Add this same place to any others?'
                : 'This place is also saved on other vehicles. Remove it from any of them too?'}
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: '#0f172a', padding: '6px 0', borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!allPicked} onChange={toggleAll} /> Select all
            </label>
            <div style={{ maxHeight: '260px', overflowY: 'auto', margin: '4px 0' }}>
              {devModal.candidates.map((c) => (
                <label key={c.deviceId} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', padding: '6px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!pickSel[c.deviceId]} onChange={(e) => setPickSel((s) => ({ ...s, [c.deviceId]: e.target.checked }))} /> {c.name}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
              <button onClick={() => setDevModal(null)} style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>{devModal.mode === 'add' ? 'Skip' : 'No, keep them'}</button>
              <button onClick={confirmDevModal} style={{ background: devModal.mode === 'add' ? 'linear-gradient(135deg,#0066ff,#0047d0)' : '#dc2626', color: 'white', padding: '8px 16px', borderRadius: '6px', fontWeight: 700, border: 'none', cursor: 'pointer' }}>{devModal.mode === 'add' ? 'Add to selected' : 'Remove from selected'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
